import { Box, Flex, Stack, Tooltip } from '@mantine/core';
import { Info } from 'react-feather';
import { useTheme } from '@/assets/styles/themeMantine';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useAppSelector } from '@/app/hooks';
import { useUserClientContext } from '@/app/clientContext';
import { getNetworkGroupLabel } from '@/helpers/networkGroups';
import styles from './ListSummary.module.css';
import { buildSummaryText } from './listSummaryHelpers';
import { useMemo } from 'react';

interface ListSummaryProps {
  unitCount: number;
  distance: number;
  label?: string;
  infoTooltip?: string;
  /** When true, hides stale counts and shows only the group/project label */
  isLoading?: boolean;
}

export const ListSummary = ({
  unitCount,
  distance,
  label,
  infoTooltip,
  isLoading = false,
}: ListSummaryProps) => {
  const theme = useTheme();
  const intl = useClientTerminology();

  const { context } = useUserClientContext();
  const { selectedNetworkGroup, spanNetworkGroup, activeGroupName } =
    useAppSelector((s) => s.analysisMap);

  const activeGroup = activeGroupName ? spanNetworkGroup : selectedNetworkGroup;

  // Always reflects the top-level grouping the user is browsing (e.g. "Circuit", "Substation")
  // regardless of whether they've drilled into a specific group.
  const groupTypeLabel = getNetworkGroupLabel(
    selectedNetworkGroup,
    context,
    intl,
    'singularTitle'
  );

  // Get unit labels from network group
  const unitLabel = useMemo(
    () =>
      getNetworkGroupLabel(
        activeGroup,
        context,
        intl,
        unitCount === 1 ? 'singularInline' : 'pluralInline'
      ),
    [activeGroup, context, intl, unitCount]
  );

  // Format the distance number
  const formattedDistance = intl.formatNumber(distance, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  // Get appropriate distance translation key based on metric
  const distanceTranslationKey =
    context.analysis_config.metric === 'ft' ? 'noun.miles' : 'noun.kilometers';

  // The prefix is the group/project label shown before the span count.
  const prefix = label ?? (activeGroupName ? groupTypeLabel : null);

  const count = intl.formatNumber(unitCount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const dist = `${formattedDistance} ${intl.formatMessage({ id: distanceTranslationKey })}`;

  // When loading show only the prefix label; when ready show the full "N spans • X km" text.
  const summaryText = isLoading
    ? prefix
    : buildSummaryText(prefix, count, unitLabel, dist);

  if (!isLoading && (!unitCount || unitCount <= 0)) return null;
  if (summaryText == null) return null;

  return (
    <Stack
      className={styles.summary}
      align="flex-start"
      data-testid="list-summary"
      gap="xs"
    >
      <Box
        c={theme.other.mainColor.foreground.secondary}
        data-testid="list-summary-total"
      >
        {prefix != null ? (
          <Flex align="center" gap={4} style={{ display: 'inline-flex' }}>
            <span>{summaryText}</span>
            {!isLoading && infoTooltip && (
              <Tooltip
                label={infoTooltip}
                multiline
                w={240}
                withArrow
                position="bottom-start"
              >
                <button
                  type="button"
                  aria-label="More information"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'help',
                  }}
                >
                  <Info size={14} />
                </button>
              </Tooltip>
            )}
          </Flex>
        ) : (
          summaryText
        )}
      </Box>
    </Stack>
  );
};
